import type { AppLanguage } from "./types";
/**
 * API helpers for the shop-floor-mode feature (§5.1 – §5.3).
 *
 * Three authentication modes are used across these endpoints:
 *
 *   request()       — JWT₁ Bearer (CRM login token, standard retry on 401)
 *   armedRequest()  — X-Shop-Floor-Session-Id only, no JWT₁
 *   floorRequest()  — JWT₃ Bearer (floor token issued after PIN verify)
 */
import {
  request,
  armedRequest,
  floorRequest,
  floorFetchBouquetPhotoBlobUrl,
  floorFetchItemImageBlobUrl,
  floorFetchItemImageBlobUrlCached,
  floorRemoveBackground,
  floorFetchWastePhotoBlobUrl,
  floorFetchBouquetTemplateImageBlobUrl,
  floorFetchTaskImageBlobUrl,
  floorFetchReceiptLogoBlobUrl,
  floorFetchOrderReferencePhotoBlobUrl,
} from "./apiClient";
import { appendKioskGeo, withKioskGeo } from "./kioskGeo";
import type { ReceiptData } from "../receipt/receiptLayout";
import {
  releaseItemImageBlobUrl,
  type ItemImageVariant,
} from "../utils/itemImageBlobCache";
import type {
  BelowFloorFlag,
  ActiveBatchRow,
  AssignableUser,
  BouquetBuildPhoto,
  BouquetBuilderItem,
  BouquetPaperUsagePayload,
  BouquetTemplate,
  Color,
  CreateSaleLinePayload,
  CustomerOrder,
  CaptureDeliveryLineBarcodePayload,
  ConfirmDeliveryLinePayload,
  DefaultUom,
  DeliveryJob,
  InventoryBalance,
  FloorTodayWorkItem,
  ItemCategory,
  Location,
  LogWastePayload,
  OrderReferencePhoto,
  PaperRollRow,
  PaymentPart,
  SalePayment,
  Shift,
  ScheduledDelivery,
  ShopifyOrderView,
  ShopifyPassedAway,
  SubCategory,
  Task,
  TaskSchedule,
  UpdateTaskSchedulePayload,
  StaffOrderPaymentMethod,
  ReceivedBatchMoney,
  TypedMoneyBasis,
  LotCostSource,
  ZoneType,
} from "./types";

// ── Response types ───────────────────────────────────────────────────────────

export interface EnterShopModeResponse {
  shopFloorSessionId: string;
  locationId: string;
  /** ISO-8601 UTC — the ShopFloorSession hard expiry (12 h from now). */
  expiresAt: string;
}

export interface ShopModeStatusResponse {
  locationId: string;
  /** ISO-8601 UTC. */
  expiresAt: string;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
}

/**
 * G-79 — what the LOCKED kiosk screen may know about waiting work.
 *
 * Four fields per order and nothing else: no customer, no address, no lines.
 * The locked screen is the least-authenticated surface in the product, so it
 * carries the least data.
 */
export interface WaitingOrder {
  id: string;
  kind: "SHOPIFY_UNCLAIMED" | "SHOP_ORDER";
  /** null when no price has been agreed. Never a guessed number. */
  amountEur: string | null;
  since: string;
}

export interface WaitingOrders {
  /** Readonly: a response is evidence, and nothing may edit it into a lie. */
  orders: readonly WaitingOrder[];
  totalEur: string;
  /** True when some order has no price, so the total is a floor not a sum. */
  totalIsPartial: boolean;
}

export interface FloorActor {
  id: string;
  fullName: string;
  role: string;
  /**
   * G-85 S6 — the language this person is shown the app in, or null when they
   * have never chosen one (the kiosk then keeps showing the tablet's).
   */
  language?: AppLanguage | null;
}

/** G-48: a fob's lifecycle for the Team page. Never carries a secret. */
export interface FobSummary {
  id: string;
  issuedAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export interface FloorJwtResponse {
  /** Signed JWT₃ — typ:"floor", hard exp = now+10 min; server enforces 2-min idle. */
  token: string;
  actor: FloorActor;
  /** ISO-8601 UTC hard expiry of the JWT₃. */
  expiresAt: string;
}

// ── G-84 storage boxes ──────────────────────────────────────────────────────

/** One box as the list, the scan and the card see it. */
export interface FloorBox {
  id: string;
  code: string;
  /** What the label's barcode says, e.g. `BX000017`. */
  barcodeText: string;
  name: string | null;
  status: "ACTIVE" | "RETIRED";
  locationId: string;
  locationName: string;
  zoneId: string;
  keptInZone: { id: string; name: string } | null;
  issuedAt: string;
  issuedByName: string;
  retiredAt: string | null;
}

export interface FloorBoxSummary extends FloorBox {
  itemCount: number;
  totalUnits: string;
}

export interface FloorBoxContentsItem {
  itemId: string;
  name: string;
  imageKey: string | null;
  /** G-85 S5: the kiosk translates by code; the label is its fallback. */
  categoryCode: string;
  categoryLabel: string;
  defaultUom: DefaultUom;
  barcode: string | null;
  qtyOnHand: string;
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
  /** A manager's fact; absent for a florist. */
  defaultPurchaseUnitCostEur?: string | null;
}

export interface FloorBoxContents {
  box: FloorBox;
  itemCount: number;
  totalUnits: string;
  items: FloorBoxContentsItem[];
}

export interface FloorBoxLabelData {
  code: string;
  barcodeText: string;
  name: string | null;
  shopName: string;
  keptInZoneName: string | null;
}

/** OD-47: one answer to "Is this from box X?". */
export interface BoxAnswer {
  boxId: string;
  takenFromBox: boolean;
}

/** The 409 body a consuming request returns when a box needs an answer. */
export interface BoxConfirmationRequiredBody {
  code: "BOX_CONFIRMATION_REQUIRED";
  message: string;
  boxes: {
    boxId: string;
    code: string;
    name: string | null;
    items: { itemId: string; itemName: string; qty: string }[];
  }[];
}
/**
 * G-89: a catalogue row the shop does not stock. Same shape the finder needs
 * to draw a result, minus anything about quantity — there is none.
 */
export interface FloorCatalogueItem {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  imageKey: string | null;
  defaultUom: DefaultUom;
  isPerishable: boolean;
  /** G-85 S5: the kiosk translates by code; the label is its fallback. */
  categoryCode: string;
  categoryLabel: string;
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
}

/**
 * G-89: the floor plane's thumbnail pair, in the shape `ItemThumb` accepts.
 *
 * Module-level and frozen in identity on purpose — the component keeps it in a
 * ref, and a fresh literal per render would churn the shared blob cache.
 */
export const FLOOR_ITEM_THUMB_SOURCE = {
  fetchThumbUrl: (itemId: string, imageKey: string) =>
    shopModeApi.floorItemImageThumbUrl(itemId, imageKey),
  releaseThumbUrl: (itemId: string, imageKey: string) =>
    shopModeApi.floorReleaseItemImageThumb(itemId, imageKey),
};

/** Result of resolving a scanned barcode via GET /shop-mode/items/by-barcode. */
export interface FloorScannedItem {
  id: string;
  name: string;
  /**
   * G-61: the SKU is what tells two variants apart when they share a
   * barcode, so the picker can show it.
   */
  sku: string | null;
  /** Storage key for the product photo, or null if none uploaded. */
  imageKey: string | null;
  /** Perishables hide the "Add to stock" action (flower-batch flow only). */
  isPerishable: boolean;
  defaultSaleUnitPriceEur: string | null;
  /**
   * G-45 Tier 2: what this actually sells for — the typed price when there is
   * one, otherwise derived from real batch cost. Null only when neither exists.
   */
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
  category: { code: string; label: string };
}

/** G-80: why the Photo button is absent, when it is. */
export type FloorPhotoSearchUnavailableReason =
  | "disabled"
  | "not_configured"
  | "cap_reached"
  | "provider_error"
  | "timeout"
  | "refusal"
  | "bad_output";

export type FloorPhotoSearchStatus =
  | { available: true }
  | { available: false; reason: FloorPhotoSearchUnavailableReason };

/** G-80: an item the model thinks the photo could be — the scanned-item row plus its verdict. */
export interface FloorPhotoSearchCandidate extends FloorScannedItem {
  match: "EXACT" | "SIMILAR";
  reason: string;
  /** 1-based, best first. */
  rank: number;
  totalQty: string;
  places: { zoneName: string; qty: string }[];
}

export interface FloorPhotoSearchResult {
  /** Null when nothing searchable is in stock — no model was asked, nothing was stored. */
  searchId: string | null;
  stockListSize: number;
  /** Null when no model was asked. */
  isFlower: boolean | null;
  flowerType: string | null;
  colour: string | null;
  confidence: "HIGH" | "MEDIUM" | "LOW" | null;
  notes: string | null;
  candidates: FloorPhotoSearchCandidate[];
}

export interface FloorPhotoSearchPickResult {
  searchId: string;
  outcome: "PENDING" | "PICKED_CANDIDATE" | "PICKED_OTHER" | "DISMISSED";
  chosenItemId: string | null;
  chosenRank: number | null;
}

/**
 * G-73 — the kiosk item card.
 *
 * `defaultPurchaseUnitCostEur` and each arrival's `purchaseUnitCostEur` are
 * OPTIONAL in this type because the server omits them entirely for a florist:
 * purchase cost is a manager's fact, and an absent key renders no row at all
 * (a null would render an empty one, which is a different claim).
 */
export interface FloorItemCard {
  item: {
    id: string;
    name: string;
    description: string | null;
    producer: string | null;
    sku: string | null;
    barcode: string | null;
    /**
     * G-99: the code the SHOP issued for this item, or null when it has none.
     * Independent of `barcode` above and never a replacement for it — an EAN is
     * the manufacturer's and may name several items (G-61), while this one
     * names exactly this item.
     */
    issuedBarcode: { code: string; barcodeText: string } | null;
    /** `vatRatePercent` (G-121 S3): named by the receive price's IVA switch. */
    category: { id: string; code: string; label: string; vatRatePercent: string };
    subCategory: { id: string; code: string; label: string } | null;
    color: { id: string; name: string; hex: string } | null;
    defaultUom: DefaultUom;
    isPerishable: boolean;
    isActive: boolean;
    stemHeightCm: string | null;
    diameterCm: string | null;
    depthCm: string | null;
    reorderThreshold: string | null;
    imageKey: string | null;
    defaultSaleUnitPriceEur: string | null;
    saleUnitPriceEur: string | null;
    saleUnitPriceIsDerived: boolean;
    defaultPurchaseUnitCostEur?: string | null;
  };
  stock: {
    totalQtyOnHand: string;
    zones: {
      zoneId: string;
      zoneName: string;
      zoneIsActive: boolean;
      /** G-84: "BOX" when this zone is a storage box. */
      zoneType: ZoneType;
      qtyOnHand: string;
      /**
       * G-84 (owner 2026-09-17): the box this place IS, when it is one — the
       * code on its label and the zone it is kept in, so the card can say
       * "BOX-000017 · kept in Storage" instead of a code with no address.
       */
      box?: { code: string; keptInZoneName: string | null } | null;
    }[];
  };
  arrivals: {
    id: string;
    batchCode: string;
    receivedAt: string;
    remainingQty: string;
    receivedQty: string;
    zoneName: string | null;
    supplierName: string | null;
    receivedByName: string | null;
    status: string;
    /** WO-B part 4: null when the lot's cost is unknown; absent for a florist. */
    purchaseUnitCostEur?: string | null;
    /** WO-B part 7: priced, estimated or unknown; absent for a florist. */
    costSource?: LotCostSource;
  }[];
  /**
   * G-86: every active zone in this shop — where a count or a receive MAY be
   * recorded, which is not the same as where this item already is. An item
   * with no stock here still has somewhere to be counted onto.
   */
  shopZones: { zoneId: string; zoneName: string; zoneType: ZoneType }[];
  /** Whether a typed quantity correction is possible, and why not when it is not. */
  quantityCorrection: { allowed: boolean; refusal: string | null };
  permissions: { canManage: boolean; editableFields: string[] };
}

/** The fields the card may send back. The server is the authority on who may. */
export type FloorItemEditPayload = {
  description?: string | null;
  name?: string;
  defaultSaleUnitPriceEur?: number | null;
  isActive?: boolean;
};

/** What logging a waste answers with — `movementId` addresses its photo. */
export interface FloorWasteResult {
  ok: boolean;
  batchId: string;
  remainingQty: string;
  movementId: string;
  hasPhoto: boolean;
}

/** One non-perishable catalogue item available to add to a floor ticket. */
export interface FloorSellableItem {
  id: string;
  name: string;
  imageKey: string | null;
  defaultUom: DefaultUom;
  defaultSaleUnitPriceEur: string | null;
  /** G-45 Tier 2: typed price if set, else derived from cost; null if neither. */
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
  /** Stock summed across every zone at the armed shop. */
  qtyOnHand: string;
  hasBarcode: boolean;
  category: { id: string; code: string; label: string };
  color: { id: string; name: string; hex: string | null } | null;
}

/**
 * One tap-to-add card in the shop-floor bouquet builder. Canonical definition
 * now lives in `./types` (shared with the phone builder); re-exported here so
 * existing floor imports (`from "../../api/shopMode"`) keep working.
 */
export type { BouquetBuilderItem } from "./types";

/**
 * G-175 (KI-155): one draft line's stem cost as saving would take it — the
 * known part (null when no unit of it has a known cost), the units drawn from
 * lots of unknown cost, and whether the shop has enough of it at all.
 */
export interface StemCostEstimate {
  itemId: string;
  quantity: string;
  knownCostEur: string | null;
  unknownCostQty: string;
  enoughStock: boolean;
}

/** Body for POST /shop-mode/bouquet/complete — location/zone forced server-side. */
export interface ShopFloorBuildBouquetPayload {
  templateId?: string;
  lines?: Array<{ itemId: string; quantity: number; uom: DefaultUom }>;
  paperUsages?: BouquetPaperUsagePayload[];
  name?: string;
  notes?: string;
  /** G-84 (OD-47): answers to "Is this from box X?", resubmitted after a 409. */
  boxAnswers?: BoxAnswer[];
}

export interface ShopFloorBuildResult {
  buildId: string;
  name: string | null;
  /** Authoritative total cost-to-build (COGS), computed server-side at save. */
  totalCogsEur: string | null;
  /**
   * WO-B (OD-198): components whose cost is unknown. Above 0, `totalCogsEur`
   * is only the known part.
   */
  unknownCostComponentCount: number;
  /** Sum of component unit selling prices, computed and snapshotted server-side. */
  suggestedSalePriceEur: string | null;
}

/** One exact item consumed by a FINALIZED build (e.g. 12 × Red Naomi Rose). */
export interface FloorBouquetInStockLine {
  id: string;
  itemName: string;
  quantity: string;
  uom: DefaultUom;
}

/**
 * One FINALIZED build sellable at this location. Powers the floor sale
 * panel's bouquet picker and the hub "Bouquets" stock view (photo comes from
 * the template image when the build followed a template).
 */
export interface FloorBouquetInStock {
  id: string;
  name: string | null;
  notes: string | null;
  /** OD-31: set when the build belongs to a customer order — listed as
   *  stock, but never sellable as a walk-in (the order hands it over). */
  forOrderCode: string | null;
  forShopify: boolean;
  /** G-45 part 3: the hard floor for this bouquet (cogs + VAT), so the
   *  price field can warn BEFORE the sale. Null when cost is unknown. */
  floorPriceEur: string | null;
  /** WO-B (OD-198): how many components have no cost. Above 0 the floor
   *  reads "cannot check" and `totalCogsEur` is only the known part. */
  unknownCostComponentCount: number;
  /** WO-B (OD-198): the same fact as a flag (count above 0). */
  costIncomplete: boolean;
  templateId: string | null;
  templateName: string | null;
  templateImageKey: string | null;
  totalCogsEur: string | null;
  suggestedSalePriceEur: string | null;
  builtByName: string;
  createdAt: string;
  lines: FloorBouquetInStockLine[];
}

/** Floor sale payload — no locationId (forced server-side) and no soldAt. */
export interface ShopFloorCreateSalePayload {
  /** G-141 (OD-215): one part, or cash + card adding up to the total. */
  payments: PaymentPart[];
  notes?: string;
  lines: CreateSaleLinePayload[];
  /** G-84 (OD-47): answers to "Is this from box X?", resubmitted after a 409. */
  boxAnswers?: BoxAnswer[];
}

/** G-156: what the kiosk may read of the shop's receipt design. */
export interface FloorReceiptTemplate {
  blocks: unknown[] | null;
  logoKey: string | null;
  logoScale: number;
}

export interface FloorSaleResult {
  id: string;
  totalEur: string;
  commissionPercent: string | null;
  commissionEarnedEur: string;
  lineCount: number;
  payments: SalePayment[];
  soldAt: string;
  /** G-45 part 3: lines below their floor — the kiosk prompts the florist. */
  belowFloor: BelowFloorFlag[];
}

/** One courier a florist can dispatch to (vehicle + availability only). */
export interface FloorAssignableCourier {
  id: string;
  fullName: string;
  phoneNumber: string | null;
  vehicleType: string | null;
  vehiclePlate: string | null;
  isAvailable: boolean;
}

export interface FloorPayoutPreview {
  hasRule: boolean;
  requiresDistance: boolean;
  payoutEur: string | null;
  marginEur: string | null;
}

export interface FloorShiftOverviewEntry {
  id: string;
  status: Shift["status"];
  plannedStart: string;
  plannedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
}

export interface FloorTodayOverview {
  asOf: string;
  businessDate: string;
  timeZone: string;
  activeShift: FloorShiftOverviewEntry | null;
  nextScheduledShift: FloorShiftOverviewEntry | null;
  latestCompletedShift: FloorShiftOverviewEntry | null;
  clockInAvailable: boolean;
  workedMinutesToday: number;
  /** null in a salaried month (KI-169). */
  wageEarnedTodayEur: string | null;
  commissionEarnedTodayEur: string;
  totalEarnedTodayEur: string | null;
  missingHourlyRate: boolean;
  /** KI-169: this month pays a salary; the card shows no hourly figure. */
  salaried: boolean;
  /** false for owners and admins (OD-151): the panel shows no pay at all. */
  payShown: boolean;
}

// ── API ──────────────────────────────────────────────────────────────────────

export const shopModeApi = {
  /**
   * POST /v1/shop-mode/enter
   * Arms the tablet. Requires JWT₁ (Manager/Owner is still CRM-authenticated).
   * Returns the ShopFloorSessionId the SPA must store in sessionStorage.
   */
  async enter(body: { locationId?: string }): Promise<EnterShopModeResponse> {
    return request<EnterShopModeResponse>("/shop-mode/enter", {
      method: "POST",
      body: await withKioskGeo(body),
    });
  },

  /**
   * DELETE /v1/shop-mode/exit
   * Disarms the tablet. Requires JWT₁ + X-Shop-Floor-Session-Id (includeSfs: true
   * is handled inside request() when the option is set).
   */
  async exit(shopFloorSessionId: string): Promise<void> {
    return request<void>("/shop-mode/exit", {
      method: "DELETE",
      includeSfs: true,
      shopFloorSessionId,
      body: await withKioskGeo({}),
    });
  },

  /**
   * GET /v1/shop-mode/status
   * Returns live session state. No JWT₁ required — SFS header only.
   * Used on page load to rehydrate ShopModeContext after a full reload.
   */
  status(shopFloorSessionId: string): Promise<ShopModeStatusResponse> {
    return armedRequest<ShopModeStatusResponse>(
      "/shop-mode/status",
      shopFloorSessionId,
    );
  },

  /**
   * GET /v1/shop-mode/waiting
   * Is an order waiting at this shop? SFS header only — this is the one read
   * the tablet may make with nobody signed in, so a florist walking past sees
   * that work arrived without unlocking anything (owner, 2026-09-13).
   */
  waiting(shopFloorSessionId: string): Promise<WaitingOrders> {
    return armedRequest<WaitingOrders>(
      "/shop-mode/waiting",
      shopFloorSessionId,
    );
  },

  /**
   * POST /v1/shop-mode/pin/verify
   * Verifies a floor worker's 4-char PIN and issues JWT₃.
   * No JWT₁ required — SFS header only.
   */
  async verifyPin(
    pin: string,
    shopFloorSessionId: string,
  ): Promise<FloorJwtResponse> {
    return armedRequest<FloorJwtResponse>(
      "/shop-mode/pin/verify",
      shopFloorSessionId,
      {
        method: "POST",
        body: await withKioskGeo({ pin }),
      },
    );
  },

  /**
   * POST /v1/shop-mode/fob/verify
   *
   * G-48 — the tap door. Same armed-session credential as the PIN, and the
   * server applies the same progressive delay to both, so a fob is not a way
   * around the PIN's throttle.
   */
  async verifyFob(
    payload: string,
    counter: number | undefined,
    shopFloorSessionId: string,
  ): Promise<FloorJwtResponse> {
    return armedRequest<FloorJwtResponse>(
      "/shop-mode/fob/verify",
      shopFloorSessionId,
      {
        method: "POST",
        body: await withKioskGeo(
          counter === undefined ? { payload } : { payload, counter },
        ),
      },
    );
  },

  /**
   * POST /v1/shop-mode/pin/lock
   * Client-initiated idle lock. Revokes the current ShopPinSession.
   * Authenticated by JWT₃ — the jti is read server-side from the floor token.
   */
  lockPin(): Promise<void> {
    return floorRequest<void>("/shop-mode/pin/lock", { method: "POST" });
  },

  /** GET /shop-mode/location — JWT₃ floor token; mirrors `/locations/:id` for armed shop */
  floorLocation(): Promise<Location> {
    return floorRequest<Location>("/shop-mode/location");
  },

  /** GET /shop-mode/item-categories — read-only taxonomy for kiosk filters */
  floorListItemCategories(): Promise<ItemCategory[]> {
    return floorRequest<ItemCategory[]>("/shop-mode/item-categories");
  },

  /**
   * GET /shop-mode/inventory/balance — location forced server-side; optional catalog / vessel filters
   * mirror CRM `GET /inventory/balance`.
   */
  floorBalance(filters: {
    zoneId?: string;
    categoryId?: string;
    minDiameterCm?: number;
    minDepthCm?: number;
  }): Promise<InventoryBalance[]> {
    return floorRequest<InventoryBalance[]>("/shop-mode/inventory/balance", {
      query: {
        ...(filters.zoneId ? { zoneId: filters.zoneId } : {}),
        ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
        ...(filters.minDiameterCm != null
          ? { minDiameterCm: String(filters.minDiameterCm) }
          : {}),
        ...(filters.minDepthCm != null ? { minDepthCm: String(filters.minDepthCm) } : {}),
      },
    });
  },

  floorListActiveBatches(params: {
    itemId: string;
    zoneId?: string;
  }): Promise<ActiveBatchRow[]> {
    return floorRequest<ActiveBatchRow[]>("/shop-mode/inventory/active-batches", {
      query: {
        itemId: params.itemId,
        zoneId: params.zoneId,
      },
    });
  },

  /**
   * G-73: `photo` is the picture of what is being thrown away — optional, and
   * when it is absent this posts exactly the JSON body it always did. The
   * route takes either; a camera that will not open must never stop a loss
   * being recorded.
   */
  async floorLogWaste(
    payload: LogWastePayload,
    photo?: File | null,
  ): Promise<FloorWasteResult> {
    if (!photo) {
      return floorRequest<FloorWasteResult>("/shop-mode/inventory/waste", {
        method: "POST",
        body: await withKioskGeo(payload),
      });
    }
    const form = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value == null) continue;
      form.append(key, String(value));
    }
    form.append("photo", photo);
    return floorRequest<FloorWasteResult>("/shop-mode/inventory/waste", {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  /** G-73: the photo of a logged waste, as an object URL the caller revokes. */
  floorWastePhotoBlobUrl(movementId: string): Promise<string | null> {
    return floorFetchWastePhotoBlobUrl(movementId);
  },

  /**
   * G-75: record that somebody looked a price up — one call per COMPLETED
   * look-up (a scan that resolved, a typed search that settled), never per
   * keystroke.
   *
   * Deliberately fire-and-forget: a florist with a customer waiting must not
   * be made to wait on an audit write, and a failed log row must never look
   * like a failed search. Errors are swallowed on purpose.
   */
  async floorRecordLookup(payload: {
    how: "SCAN" | "SEARCH";
    query?: string;
    itemIds: string[];
  }): Promise<void> {
    try {
      await floorRequest("/shop-mode/items/lookups", {
        method: "POST",
        body: await withKioskGeo(payload),
      });
    } catch {
      /* A lost log row is not worth a broken price check. */
    }
  },

  // ── Photo search (G-80) ────────────────────────────────────────────────────

  /**
   * GET /shop-mode/photo-search/status — whether the Photo button should exist
   * at all. Off, unconfigured, or over the monthly budget all mean "no button",
   * never a button that fails (G-27's rule).
   */
  floorPhotoSearchStatus(): Promise<FloorPhotoSearchStatus> {
    return floorRequest<FloorPhotoSearchStatus>("/shop-mode/photo-search/status");
  },

  /**
   * POST /shop-mode/photo-search — the still from the camera, as multipart.
   * One cloud call on the server; expect a few seconds. A 503 carries a
   * `reason` in its body and a plain sentence in `message`.
   */
  async floorPhotoSearch(photo: Blob): Promise<FloorPhotoSearchResult> {
    const form = new FormData();
    form.append("photo", photo, "photo.jpg");
    return floorRequest<FloorPhotoSearchResult>("/shop-mode/photo-search", {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  /**
   * POST /shop-mode/photo-search/:id/pick — the florist's answer. `null` is
   * "none of these". The server writes the ITEM_LOOKED_UP row for a pick, so
   * the caller must NOT also call `floorRecordLookup` — that would double it.
   */
  async floorPhotoSearchPick(
    searchId: string,
    itemId: string | null,
  ): Promise<FloorPhotoSearchPickResult> {
    return floorRequest<FloorPhotoSearchPickResult>(
      `/shop-mode/photo-search/${encodeURIComponent(searchId)}/pick`,
      { method: "POST", body: await withKioskGeo(itemId ? { itemId } : {}) },
    );
  },

  // ── The kiosk item card (G-73) ─────────────────────────────────────────────

  /**
   * GET /shop-mode/items/:id/card — everything the card shows: the item, how
   * much is in this shop and where, recent arrivals, and which fields THIS
   * worker may edit. The permissions come from the server so the screen and
   * the server cannot disagree about who may change what.
   */
  floorItemCard(itemId: string): Promise<FloorItemCard> {
    return floorRequest<FloorItemCard>(
      `/shop-mode/items/${encodeURIComponent(itemId)}/card`,
    );
  },

  /** PATCH /shop-mode/items/:id — 403s on a field this worker may not change. */
  async floorUpdateItem(
    itemId: string,
    payload: FloorItemEditPayload,
  ): Promise<unknown> {
    return floorRequest(`/shop-mode/items/${encodeURIComponent(itemId)}`, {
      method: "PATCH",
      body: await withKioskGeo(payload),
    });
  },

  /** POST /shop-mode/items/:id/barcode — scanning a code onto the item. */
  async floorSetItemBarcode(
    itemId: string,
    barcode: string,
  ): Promise<{ id: string; barcode: string; changed: boolean }> {
    return floorRequest(
      `/shop-mode/items/${encodeURIComponent(itemId)}/barcode`,
      { method: "POST", body: await withKioskGeo({ barcode }) },
    );
  },

  /**
   * POST /shop-mode/items/:id/barcode/issue — G-99: mint the shop's own code
   * for an item that arrived without a barcode.
   *
   * IDEMPOTENT. An item has one issued code for ever, so a second press
   * returns the same code with `issuedNow: false` rather than a second code.
   * Safe to retry when the tablet's answer never arrives.
   */
  async floorIssueItemBarcode(
    itemId: string,
  ): Promise<{ code: string; barcodeText: string; issuedNow: boolean }> {
    return floorRequest(
      `/shop-mode/items/${encodeURIComponent(itemId)}/barcode/issue`,
      { method: "POST", body: await withKioskGeo({}) },
    );
  },

  /** POST /shop-mode/items/:id/photo — replace the product photo from the tablet. */
  async floorReplaceItemPhoto(
    itemId: string,
    photo: File,
  ): Promise<{ id: string; imageKey: string | null }> {
    const form = new FormData();
    form.append("photo", photo);
    return floorRequest<{ id: string; imageKey: string | null }>(
      `/shop-mode/items/${encodeURIComponent(itemId)}/photo`,
      { method: "POST", body: await appendKioskGeo(form) },
    );
  },

  /**
   * POST /shop-mode/inventory/adjust — "there are 40 on the shelf, not 52".
   * Non-perishables only; the card hides the box for anything else and the
   * server refuses it regardless.
   */
  async floorAdjustStock(payload: {
    itemId: string;
    zoneId: string;
    targetQtyOnHand: number;
    /** G-86: optional — a count is a fact without a sentence attached. */
    reason?: string;
  }): Promise<unknown> {
    return floorRequest("/shop-mode/inventory/adjust", {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /**
   * GET /shop-mode/items/by-barcode — the active items carrying a scanned
   * barcode. Rejects with a 404 when the code is not in the database (caller
   * shows the "Barcode is not in the database" screen).
   *
   * G-61: a barcode can name several items, because manufacturers reuse one
   * EAN across variants. One match is the ordinary case; more than one is
   * put to the florist to tap.
   */
  async floorFindItemsByBarcode(barcode: string): Promise<FloorScannedItem[]> {
    const { items } = await floorRequest<{ items: FloorScannedItem[] }>(
      "/shop-mode/items/by-barcode",
      { query: { barcode } },
    );
    return items;
  },

  /** GET /shop-mode/sales/items-in-stock — manual register product picker. */
  floorListSellableItemsInStock(): Promise<FloorSellableItem[]> {
    return floorRequest<FloorSellableItem[]>("/shop-mode/sales/items-in-stock");
  },

  /** G-112 (OD-103): ticking an item may ask "Is this from box X?". */
  async floorSetCustomerOrderLinePicked(
    orderId: string,
    lineId: string,
    picked: boolean,
    boxAnswers: BoxAnswer[] = [],
  ): Promise<CustomerOrder> {
    return floorRequest<CustomerOrder>(
      `/shop-mode/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/picked`,
      {
        method: "PATCH",
        body: await withKioskGeo(
          boxAnswers.length ? { picked, boxAnswers } : { picked },
        ),
      },
    );
  },

  async floorCompleteCustomerOrderBouquet(
    orderId: string,
    // acceptRecipeChanges: the florist's yes after a 409 ORDER_RECIPE_CHANGE_CONFIRMATION_REQUIRED.
    payload: ShopFloorBuildBouquetPayload & { acceptRecipeChanges?: boolean },
  ): Promise<ShopFloorBuildResult & { order: CustomerOrder }> {
    return floorRequest<ShopFloorBuildResult & { order: CustomerOrder }>(
      `/shop-mode/orders/${encodeURIComponent(orderId)}/complete-bouquet`,
      { method: "POST", body: await withKioskGeo(payload) },
    );
  },

  // G-15: the manager's reference photos, read-only for the floor.
  floorListOrderReferencePhotos(
    orderId: string,
  ): Promise<OrderReferencePhoto[]> {
    return floorRequest<OrderReferencePhoto[]>(
      `/shop-mode/orders/${encodeURIComponent(orderId)}/reference-photos`,
    );
  },

  floorOrderReferencePhotoBlobUrl(
    orderId: string,
    photoId: string,
  ): Promise<string | null> {
    return floorFetchOrderReferencePhotoBlobUrl(orderId, photoId);
  },

  /**
   * POST /shop-mode/inventory/receive — single-use receiving from the floor.
   * Location is forced server-side; perishables are rejected by the API.
   */
  async floorReceiveStock(payload: {
    itemId: string;
    zoneId: string;
    quantity: number;
    /**
     * G-86: OMIT when nobody typed a price. The server then seeds the unit
     * cost from the item's last real batch or the catalogue default and
     * records spend as 0, rather than booking a receive at €0 and letting the
     * stock sell at a phantom 100% margin.
     */
    invoiceTotalEur?: number;
    /** G-121 S3: required with a price — which side of IVA it is on. */
    moneyBasis?: TypedMoneyBasis;
    /**
     * G-174 (KI-100): only with a price. Given, the receive is filed in a
     * hand-made delivery of that supplier that waits for its factura; omitted
     * ("Not on the list"), the lot stands alone.
     */
    supplierId?: string;
  }): Promise<ReceivedBatchMoney> {
    return floorRequest<ReceivedBatchMoney>("/shop-mode/inventory/receive", {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /** G-174: GET /shop-mode/suppliers — the active suppliers (managers, owners, admins). */
  async floorListSuppliers(): Promise<Array<{ id: string; name: string }>> {
    return floorRequest<Array<{ id: string; name: string }>>("/shop-mode/suppliers");
  },

  /**
   * Fetch a product photo as an object URL using the floor token.
   * Returns null when the item has no image or the request fails.
   */
  /**
   * G-89: the kiosk's own thumbnail fetch, through the SAME refcounted cache
   * the staff one uses — one request per item across every row, released on
   * unmount. Without the cache a list of forty rows would open forty object
   * URLs and never free them.
   */
  floorItemImageThumbUrl(itemId: string, imageKey: string): Promise<string | null> {
    return floorFetchItemImageBlobUrlCached(itemId, { size: "thumb", version: imageKey });
  },

  /** G-116: pass the item's `imageKey` as `version` — see `ItemImageVariant`. */
  floorItemImageBlobUrl(
    itemId: string,
    options: ItemImageVariant & { version: string },
  ): Promise<string | null> {
    return floorFetchItemImageBlobUrl(itemId, options);
  },

  // ── G-84 storage boxes ─────────────────────────────────────────────────────

  /** GET /shop-mode/boxes — this shop's active boxes with what they hold. */
  async floorListBoxes(): Promise<FloorBoxSummary[]> {
    const res = await floorRequest<{ boxes: FloorBoxSummary[] }>("/shop-mode/boxes");
    return res.boxes;
  },

  /** POST /shop-mode/boxes — issue a box (OD-46: anyone PIN'd in). */
  async floorIssueBox(payload: { name?: string | null; keptInZoneId?: string | null }): Promise<FloorBox> {
    return floorRequest<FloorBox>("/shop-mode/boxes", {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /** GET /shop-mode/boxes/by-barcode — a scanned label → this shop's box, or 404. */
  async floorFindBoxByBarcode(barcode: string): Promise<FloorBox> {
    const res = await floorRequest<{ box: FloorBox }>(
      `/shop-mode/boxes/by-barcode?barcode=${encodeURIComponent(barcode)}`,
    );
    return res.box;
  },

  /** GET /shop-mode/boxes/:id — what is inside (writes BOX_VIEWED, geo-stamped). */
  async floorBoxContents(boxId: string): Promise<FloorBoxContents> {
    const geo = await withKioskGeo({});
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(geo)) if (v != null) qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return floorRequest<FloorBoxContents>(`/shop-mode/boxes/${encodeURIComponent(boxId)}${suffix}`);
  },

  async floorUpdateBox(boxId: string, payload: { name?: string | null; keptInZoneId?: string | null }): Promise<FloorBox> {
    return floorRequest<FloorBox>(`/shop-mode/boxes/${encodeURIComponent(boxId)}`, {
      method: "PATCH",
      body: await withKioskGeo(payload),
    });
  },

  async floorRetireBox(boxId: string): Promise<FloorBox> {
    return floorRequest<FloorBox>(`/shop-mode/boxes/${encodeURIComponent(boxId)}/retire`, {
      method: "POST",
      body: await withKioskGeo({}),
    });
  },

  /** POST /shop-mode/boxes/:id/put — from the shelves into the box (OD-50). */
  async floorPutInBox(boxId: string, payload: { itemId: string; quantity: number; fromZoneId?: string }): Promise<{ box: FloorBox; itemName: string; movedQty: string }> {
    return floorRequest(`/shop-mode/boxes/${encodeURIComponent(boxId)}/put`, {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /** POST /shop-mode/boxes/:id/take-out — out of the box onto a shelf zone. */
  async floorTakeOutOfBox(boxId: string, payload: { itemId: string; quantity: number; toZoneId: string }): Promise<{ box: FloorBox; itemName: string; toZoneName: string; movedQty: string }> {
    return floorRequest(`/shop-mode/boxes/${encodeURIComponent(boxId)}/take-out`, {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  floorBoxLabel(boxId: string): Promise<FloorBoxLabelData> {
    return floorRequest<FloorBoxLabelData>(`/shop-mode/boxes/${encodeURIComponent(boxId)}/label`);
  },

  async floorBoxLabelPrinted(boxId: string): Promise<void> {
    await floorRequest(`/shop-mode/boxes/${encodeURIComponent(boxId)}/label-printed`, {
      method: "POST",
      body: await withKioskGeo({}),
    });
  },

  /**
   * G-87: hand a kiosk photo to the same background remover the CRM uses.
   * Throws on 503 when the sidecar is down, so the caller keeps the original.
   */
  floorRemoveBackground(file: File): Promise<File> {
    return floorRemoveBackground(file);
  },

  /** Pairs with `floorItemImageThumbUrl` — same cache, same key. */
  floorReleaseItemImageThumb(itemId: string, imageKey: string): void {
    releaseItemImageBlobUrl(itemId, "thumb", imageKey);
  },

  /**
   * GET /shop-mode/items/search — catalogue matches, stocked here or not.
   * Capped server-side; the caller shows them under the stock results.
   */
  async floorSearchCatalogue(q: string, limit = 12): Promise<FloorCatalogueItem[]> {
    const res = await floorRequest<{ items: FloorCatalogueItem[] }>(
      `/shop-mode/items/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    );
    return res.items;
  },

  // ── Bouquet builder (shop-floor) ───────────────────────────────────────────

  /** GET /shop-mode/bouquet/colors — the fixed 7-colour palette for the filter. */
  floorListBouquetColors(): Promise<Color[]> {
    return floorRequest<Color[]>("/shop-mode/bouquet/colors");
  },

  /** GET /shop-mode/bouquet/items — location-wide, stock-aggregated item cards. */
  floorListBouquetItems(): Promise<BouquetBuilderItem[]> {
    return floorRequest<BouquetBuilderItem[]>("/shop-mode/bouquet/items");
  },

  /**
   * POST /shop-mode/bouquet/estimate — G-175 (KI-155): the draft's stem costs
   * the way saving takes them (FIFO across lots), for the live price.
   */
  floorEstimateStemCosts(lines: Array<{ itemId: string; quantity: number }>): Promise<StemCostEstimate[]> {
    return floorRequest<StemCostEstimate[]>("/shop-mode/bouquet/estimate", {
      method: "POST",
      body: { lines },
    });
  },

  /** GET /shop-mode/bouquet/subcategories — flower-type chips for the filter. */
  floorListBouquetSubcategories(): Promise<SubCategory[]> {
    return floorRequest<SubCategory[]>("/shop-mode/bouquet/subcategories");
  },

  /** GET /shop-mode/bouquet/templates — templates available at this location. */
  floorListBouquetTemplates(colorId?: string): Promise<BouquetTemplate[]> {
    return floorRequest<BouquetTemplate[]>("/shop-mode/bouquet/templates", {
      query: { ...(colorId ? { colorId } : {}) },
    });
  },

  /** GET /shop-mode/bouquet/paper-rolls — active paper/ribbon rolls to cut from. */
  floorListBouquetPaperRolls(): Promise<PaperRollRow[]> {
    return floorRequest<PaperRollRow[]>("/shop-mode/bouquet/paper-rolls");
  },

  /** POST /shop-mode/bouquet/complete — draft → consume paper → finalize (one tx). */
  async floorBuildBouquetComplete(
    payload: ShopFloorBuildBouquetPayload,
  ): Promise<ShopFloorBuildResult> {
    return floorRequest<ShopFloorBuildResult>("/shop-mode/bouquet/complete", {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /** Fetch a bouquet-template photo as an object URL using the floor token. */
  floorBouquetTemplateImageBlobUrl(templateId: string): Promise<string | null> {
    return floorFetchBouquetTemplateImageBlobUrl(templateId);
  },

  floorListTodayTasks(): Promise<Task[]> {
    return floorRequest<Task[]>("/shop-mode/tasks/today");
  },

  floorListTodayWorkItems(): Promise<FloorTodayWorkItem[]> {
    return floorRequest<FloorTodayWorkItem[]>("/shop-mode/tasks/today-work");
  },

  floorGetDelivery(id: string): Promise<ScheduledDelivery> {
    return floorRequest<ScheduledDelivery>(
      `/shop-mode/deliveries/${encodeURIComponent(id)}`,
    );
  },

  /**
   * G-121 S2: `photo` is the picture of what arrived damaged — required with
   * damaged units and refused without them (OD-125); the server stores it on
   * the line's receive (IN) movement. Without one this posts exactly the JSON
   * body it always did; with one it sends the same fields as multipart text
   * plus the file, the way `floorLogWaste` does.
   */
  async floorConfirmDeliveryLine(
    deliveryId: string,
    lineId: string,
    payload: ConfirmDeliveryLinePayload,
    photo?: File | Blob | null,
  ): Promise<ScheduledDelivery> {
    const path = `/shop-mode/deliveries/${encodeURIComponent(deliveryId)}/lines/${encodeURIComponent(lineId)}/confirm`;
    if (!photo) {
      return floorRequest<ScheduledDelivery>(path, {
        method: "POST",
        body: await withKioskGeo(payload),
      });
    }
    const form = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value == null) continue;
      form.append(key, String(value));
    }
    form.append("photo", photo);
    return floorRequest<ScheduledDelivery>(path, {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  /** G-59: barcode capture at first receipt, on the tablet. */
  async floorCaptureDeliveryLineBarcode(
    deliveryId: string,
    lineId: string,
    payload: CaptureDeliveryLineBarcodePayload,
  ): Promise<ScheduledDelivery> {
    return floorRequest<ScheduledDelivery>(
      `/shop-mode/deliveries/${encodeURIComponent(deliveryId)}/lines/${encodeURIComponent(lineId)}/barcode`,
      { method: "POST", body: await withKioskGeo(payload) },
    );
  },

  /**
   * G-12 — photos of the finished bouquet, from the floor tablet.
   *
   * The backend for this has existed since 2026-07-28 (upload service, floor
   * and staff routes, gallery reads, 10-photo cap, sha256 dedup) and had NO
   * client layer at all, so nothing could reach it. `floorRequest` already
   * passes a `FormData` body through untouched, so this needs no new plumbing.
   */
  async floorAddBouquetPhoto(
    buildId: string,
    file: File,
  ): Promise<BouquetBuildPhoto> {
    const form = new FormData();
    form.append("photo", file);
    return floorRequest<BouquetBuildPhoto>(
      `/shop-mode/bouquet/${encodeURIComponent(buildId)}/photos`,
      { method: "POST", body: await appendKioskGeo(form) },
    );
  },

  floorListBouquetPhotos(buildId: string): Promise<BouquetBuildPhoto[]> {
    return floorRequest<BouquetBuildPhoto[]>(
      `/shop-mode/bouquet/${encodeURIComponent(buildId)}/photos`,
    );
  },

  /** Photo bytes are auth-gated, so they come back as an object URL. */
  floorBouquetPhotoBlobUrl(buildId: string, photoId: string): Promise<string | null> {
    return floorFetchBouquetPhotoBlobUrl(buildId, photoId);
  },

  async floorCreateTask(form: FormData): Promise<Task> {
    return floorRequest<Task>("/shop-mode/tasks", {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  floorListTaskAssignableUsers(): Promise<AssignableUser[]> {
    return floorRequest<AssignableUser[]>("/shop-mode/tasks/assignable-users");
  },

  floorListTaskSchedules(): Promise<TaskSchedule[]> {
    return floorRequest<TaskSchedule[]>("/shop-mode/tasks/schedules");
  },

  async floorCreateTaskSchedule(form: FormData): Promise<TaskSchedule> {
    return floorRequest<TaskSchedule>("/shop-mode/tasks/schedules", {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  async floorUpdateTaskSchedule(
    id: string,
    body: UpdateTaskSchedulePayload,
  ): Promise<TaskSchedule> {
    return floorRequest<TaskSchedule>(
      `/shop-mode/tasks/schedules/${encodeURIComponent(id)}`,
      { method: "PATCH", body: await withKioskGeo(body) },
    );
  },

  async floorDeleteTaskSchedule(id: string): Promise<{ id: string }> {
    return floorRequest<{ id: string }>(
      `/shop-mode/tasks/schedules/${encodeURIComponent(id)}`,
      { method: "DELETE", body: await withKioskGeo({}) },
    );
  },

  async floorCompleteTask(id: string, form: FormData): Promise<Task> {
    return floorRequest<Task>(`/shop-mode/tasks/${id}/complete`, {
      method: "POST",
      body: await appendKioskGeo(form),
    });
  },

  async floorCancelTask(id: string, body: { reason?: string }): Promise<Task> {
    return floorRequest<Task>(`/shop-mode/tasks/${id}/cancel`, {
      method: "POST",
      body: await withKioskGeo(body),
    });
  },

  /** GET /shop-mode/sales/bouquets-in-stock — FINALIZED builds sellable here. */
  floorListBouquetsInStock(): Promise<FloorBouquetInStock[]> {
    return floorRequest<FloorBouquetInStock[]>("/shop-mode/sales/bouquets-in-stock");
  },

  /**
   * POST /shop-mode/sales — record a live sale. Location and seller are forced
   * server-side from the floor token; payment type is required.
   */
  /**
   * G-156 (OD-34, KI-68) — the courtesy ticket of one sale, as the server
   * lays it out (`GET /shop-mode/sales/:id/receipt`): lines, total, payments,
   * who served. Scoped to the tablet's shop; another shop's sale is not found.
   */
  floorReceipt(saleId: string): Promise<ReceiptData> {
    return floorRequest<ReceiptData>(
      `/shop-mode/sales/${encodeURIComponent(saleId)}/receipt`,
    );
  },

  /** The shop's ticket design; `blocks` null means the renderer's default. */
  floorReceiptTemplate(): Promise<FloorReceiptTemplate> {
    return floorRequest<FloorReceiptTemplate>(`/shop-mode/receipt-template`);
  },

  /** Object URL for the shop's logo, or null when there is none. */
  floorReceiptLogoBlobUrl(): Promise<string | null> {
    return floorFetchReceiptLogoBlobUrl();
  },

  async floorCreateSale(
    payload: ShopFloorCreateSalePayload,
  ): Promise<FloorSaleResult> {
    return floorRequest<FloorSaleResult>("/shop-mode/sales", {
      method: "POST",
      body: await withKioskGeo(payload),
    });
  },

  /** G-45 part 3: the florist explains a below-floor price at the kiosk. */
  async floorJustifyBelowFloor(
    saleLineId: string,
    reason: string,
  ): Promise<{ id: string }> {
    return floorRequest<{ id: string }>(
      `/shop-mode/sales/lines/${encodeURIComponent(saleLineId)}/below-floor-reason`,
      { method: "POST", body: await withKioskGeo({ reason }) },
    );
  },

  /** GET /shop-mode/dispatch/couriers — couriers a florist can dispatch to. */
  floorListDispatchCouriers(): Promise<FloorAssignableCourier[]> {
    return floorRequest<FloorAssignableCourier[]>("/shop-mode/dispatch/couriers");
  },

  /** POST /shop-mode/dispatch/preview-payout — computed payout/fee/margin. */
  floorPreviewDispatchPayout(payload: {
    courierUserId: string;
    priceEur: number;
    distanceKm?: number;
  }): Promise<FloorPayoutPreview> {
    return floorRequest<FloorPayoutPreview>("/shop-mode/dispatch/preview-payout", {
      method: "POST",
      body: payload,
    });
  },

  floorListAvailableShopifyOrders(): Promise<ShopifyOrderView[]> {
    return floorRequest<ShopifyOrderView[]>("/shop-mode/shopify/orders/available");
  },

  floorListMyShopifyOrders(): Promise<ShopifyOrderView[]> {
    return floorRequest<ShopifyOrderView[]>("/shop-mode/shopify/orders/mine");
  },

  floorGetShopifyOrder(id: string): Promise<ShopifyOrderView> {
    return floorRequest<ShopifyOrderView>(
      `/shop-mode/shopify/orders/${encodeURIComponent(id)}`,
    );
  },

  async floorAcceptShopifyOrder(id: string): Promise<ShopifyOrderView> {
    return floorRequest<ShopifyOrderView>(
      `/shop-mode/shopify/orders/${encodeURIComponent(id)}/accept`,
      { method: "POST", body: await withKioskGeo({}) },
    );
  },

  async floorBuildShopifyOrderLine(
    id: string,
    lineId: string,
    payload: ShopFloorBuildBouquetPayload,
  ): Promise<ShopifyOrderView> {
    return floorRequest<ShopifyOrderView>(
      `/shop-mode/shopify/orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/build`,
      { method: "POST", body: await withKioskGeo(payload) },
    );
  },

  async floorFulfilShopifyOrder(
    id: string,
    boxAnswers: BoxAnswer[] = [],
  ): Promise<ShopifyOrderView> {
    return floorRequest<ShopifyOrderView>(
      `/shop-mode/shopify/orders/${encodeURIComponent(id)}/fulfil`,
      {
        method: "POST",
        body: await withKioskGeo(boxAnswers.length ? { boxAnswers } : {}),
      },
    );
  },

  /** G-167: orders the office passed away from this shop today. */
  floorListPassedAwayShopifyOrders(): Promise<ShopifyPassedAway[]> {
    return floorRequest<ShopifyPassedAway[]>("/shop-mode/shopify/orders/passed-away");
  },

  floorTaskImageBlobUrl(
    id: string,
    slot: "reference" | "completion",
  ): Promise<string | null> {
    return floorFetchTaskImageBlobUrl(id, slot);
  },

  floorListTodayOrders(): Promise<CustomerOrder[]> {
    return floorRequest<CustomerOrder[]>("/shop-mode/orders/today");
  },

  floorListUpcomingOrders(): Promise<CustomerOrder[]> {
    return floorRequest<CustomerOrder[]>("/shop-mode/orders/upcoming");
  },

  async floorStartOrderPreparation(id: string): Promise<CustomerOrder> {
    return floorRequest<CustomerOrder>(
      `/shop-mode/orders/${encodeURIComponent(id)}/start-preparation`,
      { method: "POST", body: await withKioskGeo({}) },
    );
  },

  /** G-46: the counter order — created without leaving kiosk mode. */
  async floorCreateOrder(body: {
    customerName: string;
    /** OD-104: required on every kiosk order. */
    customerPhone: string;
    customerEmail?: string;
    customerNotes?: string;
    bouquetName?: string;
    bouquetNotes?: string;
    fulfillment: "PICKUP" | "COURIER";
    timeType: "ASAP" | "EXACT" | "WINDOW";
    scheduledDate: string;
    scheduledFor?: string;
    scheduledUntil?: string;
    totalEur?: number;
    bouquetPriceEur?: number;
    prepaidAmountEur?: number;
    prepaidMethod?: "CASH" | "CARD";
    lines?: Array<{
      description: string;
      itemId?: string;
      /** G-112: the shelf bouquet a READY_BOUQUET line holds. */
      bouquetBuildId?: string;
      quantity?: number;
      unitPriceEur?: number;
      role?: string;
    }>;
    delivery?: {
      recipientName: string;
      recipientPhone: string;
      recipientNotes?: string;
      deliveryAddress: string;
      priceEur: number;
      /** G-112 S4: what "Send with courier" offered, kept for Schedule order. */
      packageSize?: "SMALL" | "MEDIUM" | "LARGE";
      courierUserId?: string;
      distanceKm?: number;
      overridePayoutEur?: number;
    };
  }): Promise<CustomerOrder> {
    return floorRequest<CustomerOrder>("/shop-mode/orders", {
      method: "POST",
      body: await withKioskGeo(body),
    });
  },

  /** G-46: the customer's reference photo, captured at the counter. */
  async floorUploadOrderReferencePhoto(
    orderId: string,
    file: File,
  ): Promise<{ id: string }> {
    const form = await appendKioskGeo(new FormData());
    form.append("photo", file, file.name);
    return floorRequest<{ id: string }>(
      `/shop-mode/orders/${encodeURIComponent(orderId)}/reference-photos`,
      { method: "POST", body: form },
    );
  },

  /** G-20: "Customer collected" — completes a READY pickup at the counter.
   *  OD-32: chargedTotalEur carries the counter's truth when the customer
   *  changed their mind — flagged, never blocked. */
  async floorCollectOrder(
    id: string,
    body: {
      /**
       * G-141: one part (= the whole balance) or cash + card adding up to it.
       * Left out when nothing is owed; the server refuses it missing otherwise.
       */
      payments?: PaymentPart[];
      chargedTotalEur?: number;
      /** G-147: goods added at the counter, rung up on the same sale. */
      extraLines?: CreateSaleLinePayload[];
      /** G-112 (OD-103): answers to "Is this from box X?". */
      boxAnswers?: BoxAnswer[];
    },
  ): Promise<CustomerOrder & { belowFloor?: BelowFloorFlag[] }> {
    // G-177 (KI-132): the handover's sale lines below their floor, as the till's sale returns them.
    return floorRequest<CustomerOrder & { belowFloor?: BelowFloorFlag[] }>(`/shop-mode/orders/${id}/collect`, {
      method: "POST",
      body: await withKioskGeo(body),
    });
  },

  /**
   * G-112 (OD-99): "Take payment" — money for an order recorded at the
   * counter before it leaves the shop. Never more than what is owed.
   */
  async floorTakeOrderPayment(
    id: string,
    body: { method: StaffOrderPaymentMethod; amountEur: number },
  ): Promise<CustomerOrder> {
    return floorRequest<CustomerOrder>(
      `/shop-mode/orders/${encodeURIComponent(id)}/payments`,
      { method: "POST", body: await withKioskGeo(body) },
    );
  },

  async floorMarkOrderReady(id: string): Promise<CustomerOrder> {
    return floorRequest<CustomerOrder>(
      `/shop-mode/orders/${encodeURIComponent(id)}/mark-ready`,
      { method: "POST", body: await withKioskGeo({}) },
    );
  },

  // ── Floor shift clocking (PIN clock-in / End the shift) ─────────────────────

  /** GET /shop-mode/shifts/me/today — the PIN actor's shifts today at this shop. */
  floorListMyShiftsToday(): Promise<Shift[]> {
    return floorRequest<Shift[]>("/shop-mode/shifts/me/today");
  },

  /** Server-calculated worked time and earnings for the PIN actor's business day. */
  floorTodayOverview(): Promise<FloorTodayOverview> {
    return floorRequest<FloorTodayOverview>(
      "/shop-mode/shifts/me/today-overview",
    );
  },

  /** POST /shop-mode/shifts/clock-in — server resolves today's shift and starts it. */
  async floorClockIn(): Promise<Shift> {
    return floorRequest<Shift>("/shop-mode/shifts/clock-in", {
      method: "POST",
      body: await withKioskGeo({}),
    });
  },

  /** POST /shop-mode/shifts/clock-out — ends the actor's in-progress shift. */
  async floorClockOut(): Promise<Shift> {
    return floorRequest<Shift>("/shop-mode/shifts/clock-out", {
      method: "POST",
      body: await withKioskGeo({}),
    });
  },

  /**
   * POST /shop-mode/dispatch/jobs/:id/confirm-pickup — the florist's
   * "picked up by courier" confirmation (G-21 amendment, OD-28): the shop's
   * independent witness of the handover. Replaces the retired OD-4
   * "courier is here" arrival fallback.
   */
  async floorConfirmPickup(jobId: string, boxAnswers: BoxAnswer[] = []): Promise<DeliveryJob> {
    // G-177 (KI-89): nothing leaves before its delivery can write the sale —
    // a box question is answered here and the request resent with the answers.
    return floorRequest<DeliveryJob>(
      `/shop-mode/dispatch/jobs/${encodeURIComponent(jobId)}/confirm-pickup`,
      {
        method: "POST",
        body: await withKioskGeo(boxAnswers.length ? { boxAnswers } : {}),
      },
    );
  },

  /**
   * POST /v1/shop-mode/users/:userId/pin
   * Generates a fresh, unique 8-digit PIN server-side and returns it once
   * (staff can no longer pick their own — owner decision 2026-08-04).
   */
  generateUserPin(userId: string): Promise<{ pin: string }> {
    return request<{ pin: string }>(`/shop-mode/users/${userId}/pin`, {
      method: "POST",
    });
  },

  /**
   * GET /v1/shop-mode/users/:userId/pin
   * The worker's current PIN so it can be shown to them. `pin` is null for a
   * legacy hash-only record with no viewable copy — regenerate to get one.
   */
  viewUserPin(userId: string): Promise<{ pin: string | null }> {
    return request<{ pin: string | null }>(`/shop-mode/users/${userId}/pin`);
  },

  /**
   * POST /v1/shop-mode/users/:userId/fob
   *
   * G-48 — mints a fob and returns its tag payload ONCE. The response is a live
   * credential until it reaches the tag, so it is never stored, never logged
   * and never re-fetchable; a lost fob is re-issued instead (OD-21).
   */
  issueUserFob(
    userId: string,
  ): Promise<{ fobId: string; payload: string; userName: string }> {
    return request<{ fobId: string; payload: string; userName: string }>(
      `/shop-mode/users/${userId}/fob`,
      { method: "POST" },
    );
  },

  /** GET /v1/shop-mode/users/:userId/fobs — issue history, no secrets. */
  listUserFobs(userId: string): Promise<FobSummary[]> {
    return request<FobSummary[]>(`/shop-mode/users/${userId}/fobs`);
  },

  /** DELETE /v1/shop-mode/fobs/:fobId — dead on the fob's next tap. */
  revokeFob(fobId: string): Promise<{ revoked: boolean }> {
    return request<{ revoked: boolean }>(`/shop-mode/fobs/${fobId}`, {
      method: "DELETE",
    });
  },
};
